import React, { useEffect, useState } from "react";
import { Badge, Button, Card, Space, Typography, Modal, Input, Segmented } from "antd";
import { ShoppingCartOutlined, DollarOutlined, AppstoreOutlined, EditOutlined } from "@ant-design/icons";
import { money } from "../../utils/money";
import { getDeviceSettings } from "../../services/deviceSettings";
const { Title, Text } = Typography;
const { TextArea } = Input;

type CartItem = {
  product: { id:number; name:string; price:number|string };
  qty: number;
  modifiers?: { id:number; name:string; price_delta:number|string }[];
  note?:string;
};

type OrderType = "dinein" | "takeaway" | "delivery" | null;

export default function CartSidebar({
  cart, total, onInc, onDec, onRem, onPay, createKitchenOrder, onUpdateNote,
  orderType, onChangeOrderType, orderLoading, payLoading
}: {
  cart: CartItem[];
  total: number;
  onInc: (idx:number)=>void;
  onDec: (idx:number)=>void;
  onRem: (idx:number)=>void;
  onPay: ()=>void;
  createKitchenOrder: () => void;
  onUpdateNote: (idx:number, note:string)=>void;
  orderType: OrderType;
  onChangeOrderType: (t: OrderType)=>void;
  orderLoading?: boolean;
  payLoading?: boolean;
}) {
  const [noteModal, setNoteModal] = useState<{visible: boolean; itemIndex: number; currentNote: string}>({
    visible: false,
    itemIndex: -1,
    currentNote: ""
  });
  const [disableOrderBtn, setDisableOrderBtn] = useState(false);

  // --- helper za natpis koji ide u order.note ---
  const noteLabelForType = (t: OrderType) => {
    if (t === "takeaway") return "[Za ponijeti]";
    if (t === "delivery") return "[Dostava]";
    return "[U objektu]";
  };

  // --- localStorage persist za izbor + upis u pos.order_note ---
  useEffect(() => {
    try {
      const raw = localStorage.getItem("pos.order.type") as OrderType | null;
      const initial: OrderType =
        raw === "dinein" || raw === "takeaway" || raw === "delivery" ? raw : "dinein";

      // ako parent još nije postavio, sync na parent
      if (!orderType) {
        onChangeOrderType(initial);
      }

      // uvijek zapiši i order.note natpis, da backend/parent mogu pročitati
      localStorage.setItem("pos.order.note", noteLabelForType(orderType || initial));
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getDeviceSettings(["disable_order_button"]).then(s => {
      setDisableOrderBtn(!!s["disable_order_button"]);
    }).catch(()=>{});
  }, []);

  // kad god se orderType promijeni izvana, ažuriraj pos.order_type i pos.order_note
  useEffect(() => {
    if (!orderType) return;
    try {
      localStorage.setItem("pos.order_type", orderType);
      localStorage.setItem("pos.order_note", noteLabelForType(orderType));
    } catch {}
  }, [orderType]);

  const openNoteModal = (item: CartItem, index: number) => {
    setNoteModal({
      visible: true,
      itemIndex: index,
      currentNote: item.note || ""
    });
  };
  const handleNoteSave = () => {
    onUpdateNote(noteModal.itemIndex, noteModal.currentNote);
    setNoteModal({ visible: false, itemIndex: -1, currentNote: "" });
  };
  const handleNoteCancel = () => {
    setNoteModal({ visible: false, itemIndex: -1, currentNote: "" });
  };

  // trenutna vrijednost za Segmented (default dinein)
  const currentType: OrderType = orderType || "dinein";

  return (
    <>
      <Card style={{borderRadius:4,marginBottom:0 }} bodyStyle={{ padding:12 }}>
        <Space align="center" style={{ width:"100%",zIndex:2, marginBottom:8,height:'5vh',position:'absolute',top:0,left:0,right:0,padding:12,backgroundColor:'rgb(215, 215, 215)', borderRadius:8 }}>
          <Title level={5} style={{ margin:0 }}>Korpa</Title>
          <div style={{ marginLeft:"auto" }}><Badge count={cart.length} /></div>
        </Space>

        <div style={{ height:"calc(100% - 168px)", overflow:"auto" , maxHeight:'78vh', paddingBottom:180,paddingTop:60, overflowY:"auto"}}>
          {cart.length===0 && <Text type="secondary">Prazna korpa</Text>}
          <Space direction="vertical" style={{ width:"100%" }} size={8}>
            {cart.map((it,idx)=>{
              const base = Number(it.product.price ?? 0) || 0;
              const mods = (it.modifiers||[]).reduce((s,m)=> s + (Number(m.price_delta)||0), 0);
              return (
                <Card key={idx} size="small" style={{ borderRadius:1,borderBottom:"2px dashed #cfccccff" }} bodyStyle={{ padding:10 }}>
                  <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                    <Text strong ellipsis style={{ maxWidth:140 }}>{it.product.name}</Text>
                    <div style={{ marginLeft:"auto", fontVariantNumeric:"tabular-nums" }}>{money(base + mods)} KM</div>
                  </div>
                  {!!(it.modifiers?.length) && (
                    <div style={{ fontSize:12, color:"rgb(215, 215, 215)", marginTop:4 }}>
                      {(it.modifiers||[]).map(m=>m.name).join(", ")}
                    </div>
                  )}
                  {it.note && (
                    <div style={{ fontSize:12, color:"#1890ff", marginTop:4, fontStyle:"italic" }}>
                      Napomena: {it.note}
                    </div>
                  )}
                  <Space style={{ marginTop:6 }}>
                    <Button onClick={()=>onDec(idx)} size="small">−</Button>
                    <Text>{it.qty}</Text>
                    <Button onClick={()=>onInc(idx)} size="small">+</Button>
                    <Button danger size="small" onClick={()=>onRem(idx)}>Ukloni</Button>
                    <Button
                      type="dashed"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={()=>openNoteModal(it, idx)}
                    >
                      {it.note ? "Izmeni" : "Napomena"}
                    </Button>
                  </Space>
                </Card>
              );
            })}
          </Space>
        </div>

        {/* Tip narudžbe */}
        <div style={{ position:'absolute', left:0, right:0, bottom:100, padding:12 }}>
          <Segmented
            block
            color="blue"
            style={{  textAlign:"center" ,backgroundColor:'rgb(215, 215, 215)'}}
            value={currentType}
            onChange={(val) => {
              const v = val as OrderType;
              // upiši izbor i pripadajući natpis
              try {
                localStorage.setItem("pos.order_type", String(v));
                localStorage.setItem("pos.order_note", noteLabelForType(v));
              } catch {}
              // obavijesti parent da ažurira state/payload
              onChangeOrderType(v);
            }}
            options={[
              { label: "U objektu", value: "dinein" },
              { label: "Za ponijeti", value: "takeaway" },
              { label: "Dostava", value: "delivery" },
            ]}
          />
          {/* info poruka */}
          <div style={{ marginTop:6, textAlign:"center" }}>
            <Text type="secondary" style={{ fontSize:12 }}>
              Izbor tipa se upisuje u order note (npr. {noteLabelForType(currentType)}).
            </Text>
          </div>
        </div>

        <div style={{ borderTop:"1px solid #eef0f4" , position:'absolute', bottom:0, left:0, right:0, padding:12, backgroundColor:'white', borderBottomRightRadius:18 }}>
          <div style={{ display:"flex", alignItems:"center", marginBottom:8, fontWeight:700 }}>
            <span>UKUPNO</span>
            <span style={{ marginLeft:"auto", fontVariantNumeric:"tabular-nums" }}>{money(total)} KM</span>
          </div>
          <Button
            type="primary"
            icon={<ShoppingCartOutlined />}
            block size="large"
            onClick={onPay}
            disabled={!cart.length}
            loading={!!payLoading}
          >
            Naplati
          </Button>
          <Space style={{ marginTop:10, width:"100%" }}>
            {disableOrderBtn ? null : (
              <Button
                block
                icon={<AppstoreOutlined />}
                onClick={createKitchenOrder}
                disabled={!cart.length}
                loading={!!orderLoading}
              >
                Narudžba
              </Button>
            )}
            <Button
              block
              type="dashed"
              icon={<DollarOutlined />}
              onClick={onPay}
              loading={!!payLoading}
            >
              Fiskalni račun
            </Button>
          </Space>
        </div>
      </Card>

      <Modal
        title="Dodaj napomenu"
        open={noteModal.visible}
        onOk={handleNoteSave}
        onCancel={handleNoteCancel}
        okText="Sačuvaj"
        cancelText="Odustani"
        width={400}
      >
        <TextArea
          value={noteModal.currentNote}
          onChange={(e) => setNoteModal(prev => ({ ...prev, currentNote: e.target.value }))}
          placeholder="Unesite napomenu za stavku..."
          rows={4}
          maxLength={200}
          showCount
        />
      </Modal>
    </>
  );
}
